import React, { useEffect } from 'react'
import { RenderItemProps } from './types'
import { FeedTextOverlay } from '../molecules/TextOverlay'
import { TextBubble } from '../atoms/TextBubble'
import { DebugItem, FeedDimensions } from './shared'
import { ImmersiveVideo } from '@/components/Player/Immersive'
import { useIsFocusedFeedItem, useIsNearFocusedFeedItem } from '../feedScroll'
import { useMediaQueryDown, useVolume } from '@/utils'
import { videoCache } from '@/utils/videoCache'

export const FeedVideo = (props: RenderItemProps) => {
  const { item, index, contentOverlay } = props

  const isFocused = useIsFocusedFeedItem(index)
  // The virtualizer keeps ~7 items mounted (`overscan: 3`), and every mounted player starts
  // loading its stream right away, paused or not — seven streams competing with the one on
  // screen. Only the focused item and its direct neighbours mount one; the neighbours'
  // buffer is a real head start, since it's the same player that then plays. Focus moves one
  // item at a time, so whatever becomes focused was already a neighbour.
  const isNearFocus = useIsNearFocusedFeedItem(index, 1)

  const videoController = ImmersiveVideo.use()
  const interacted = ImmersiveVideo.interactedStore.use()

  const video = item?.post?.video

  const volume = useVolume()

  useEffect(() => {
    if (!interacted) return

    if (isFocused && videoController.loaded) {
      videoController.resume()
    } else {
      videoController.pause()
    }
  }, [isFocused, videoController.loaded, interacted])

  const isSmall = useMediaQueryDown(FeedDimensions.mobileBreakpoint)

  const width = isSmall ? FeedDimensions.WIDTH_WITHOUT_SPACING : FeedDimensions.WIDTH
  const height = isSmall ? FeedDimensions.HEIGHT_WITHOUT_SPACING : FeedDimensions.HEIGHT

  const videoUrl = videoCache.use(item?.id, video)

  return (
    <>
      <DebugItem {...props} />

      {contentOverlay}

      <ImmersiveVideo
        width={width}
        height={height}
        mute={volume.muted}
        volume={volume.muted ? 0 : 1}
        controller={videoController}
        active={isFocused}
      >
        {isNearFocus ? <ImmersiveVideo.Video url={videoUrl} thumbnail={video?.thumbnail} /> : null}
        <ImmersiveVideo.Placeholder thumbnail={video?.thumbnail} style={{ width, height }} />
        <ImmersiveVideo.Error />
        {videoController.loaded ? <ImmersiveVideo.Controls /> : null}
      </ImmersiveVideo>
    </>
  )
}
